/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useState} from 'react';
import {
  DragDropContext,
  Droppable
} from 'react-beautiful-dnd';
import {makeStyles} from '@material-ui/core';
import {dlsColor} from '../../theme/theme';
import {LabelTypography} from '../../typography/LabelTypography';
import {reorder} from './DragableListUtils';
import DraggableListItem, {DraggableItems, DraggableListStyleProps} from './DraggableListItem';

/**
 * Defines the prop type definitions for the DraggableList component, which are
 * as follows:
 *
 * items?: DraggableItems: The list of items that need to be showed.
 * renderContent: Function: The function which will render the individual item in a list.
 * isDragDisabled?: string: Indicated the state of drag.
 * isDropDisabled?: boolean: Indicated the state of drop.
 * direction?: 'horizontal' | 'vertical': Indicated the direction of drag and drop.
 * noDataTxt?: string: The text that needs to show when the empty list passed to this component.
 * handleOnly?: boolean: Drags only from the handle that renderContent places.
 * onDragEnd: Function: Invoke when ever changes happens in list order.
 */
export interface DraggableListProps extends DraggableListStyleProps {
  items: DraggableItems,
  renderContent?: Function,
  isDragDisabled?: boolean,
  isDropDisabled?: boolean,
  direction?: 'horizontal' | 'vertical',
  noDataTxt?: string,
  listClassName?: string,
  handleOnly?: boolean,
  onDragEnd: Function
};

// defines the style definitions for DraggableList
const useStyles = makeStyles(theme => ({
  noDataTxt : {
    margin        : ({noDataTxtMargin}: DraggableListStyleProps) => noDataTxtMargin || 'auto',
    fontSize      : '16px',
    letterSpacing : 0,
    color         : dlsColor('grey4'),
    alignContent  : 'center',
    minHeight     : '36px'
  }
}));

/**
 * Defines the DraggableList component. This component is reusable to show the list of items
 * or list of components that needs to as a list and draggable for reordering of list
 *
 * ```typescript
 *
 *  import {DraggableList} from '@ion/dls/src/draggable';
 *
 *  <DraggableList items={items}
 *    isDragDisabled={false}
 *    noDataTxt={'No data to display'}
 *    direction={'vertical'}
 *    isDropDisabled={false}
 *    renderContent={renderAddedWidget}
 *    onDragEnd={onDragEnd} />
 *
 * ```
 */
const DraggableList = memo(({
  items,
  renderContent,
  onDragEnd,
  isDragDisabled,
  direction,
  noDataTxt,
  listClassName,
  isDropDisabled = false,
  ...props
}: DraggableListProps) => {
  const classes                             = useStyles(props);
  const [draggableItems, setDraggableItems] = useState([]);

  useEffect(() => {
    setDraggableItems(items);
  }, [items]);

  /**
   * @private
   */
  const onDragChange = function(target) {
    const {destination, source} = target;
    // dropped outside the list
    if (!destination) return;
    const newItems: DraggableItems = reorder(items, source.index, destination.index);
    setDraggableItems(newItems);
    onDragEnd(newItems);
  };

  return (
    <>
      {draggableItems?.length ? (
        <DragDropContext onDragEnd={onDragChange}>
          <Droppable isDropDisabled={isDropDisabled}
            direction={direction}
            droppableId='droppable-list'>
            {provided => (
              <div ref={provided.innerRef}
                {...provided.droppableProps}
                className={listClassName}
                style={listClassName ? undefined : {width: '99%'}}>
                {draggableItems.map((item, index) => (
                  <DraggableListItem item={item}
                    isDragDisabled={isDragDisabled}
                    renderContent={renderContent}
                    index={index}
                    key={item.id?.toString()}
                    {...props} />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        (noDataTxt && (
          <LabelTypography variantType='label2' className={classes.noDataTxt}>
            {noDataTxt}
          </LabelTypography>
        ))
      )}
    </>
  );
});

export default DraggableList;
